import React, { useCallback, useEffect } from 'react'
import { Hook } from '../hooks/Hook'
import { useSingletonMVUReducer } from '../mvuReducer'
import {
  applyAction,
  makeSideEffectExecutor,
  initialize,
  WebRecorder2Action,
  UploadAttrs,
  subjectId,
  StartRecordingPolicy,
} from './webRecorder2'
import {
  broadcastRecordingState,
  clearCriticalBanner,
  setCriticalBanner,
  setSubjectId,
  trackAnalytics,
  uploadRecording,
} from './liveView'
import { Loading } from '~/components/Loading'
import { AudioState } from './recorder'
import { useWebRecorderPubSubSubscribe } from './useWebRecorderPubSubSubscribe'
import { mostRecentUnsubmittedRecording } from './recordingStorage'
import { extractSubjectId } from '~/utils/string'

export function WebRecorder2({ hook }: { hook: Hook }): React.ReactElement {
  const canUseWebRecorder = hook.el.hasAttribute('data-can-use-web-recorder')
  const clientId: string = hook.el.dataset.clientId!

  const { subjectId: defaultSubjectId, startRecording } = canUseWebRecorder
    ? defaultSubject()
    : { subjectId: undefined, startRecording: 'no' as const }

  // TODO: This needs to list the subject IDs that are being recorded
  // const otherRemoteClientIsRecording = hook.el.hasAttribute('data-other-client-is-recording')

  const upload = useCallback((attrs: UploadAttrs) => uploadRecording(hook, attrs), [hook])

  const recordingDisclosureAudioPath = hook.el.dataset.recordingDisclosureAudioPath ?? ''

  useEffect(() => {
    const handler = async () => {
      await broadcastRecordingState(hook, 'stopped')
    }

    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [])

  const callbacks = {
    upload,
    /* eslint-disable-next-line no-console */
    log: (message: string) => console.log(message),
    trackAnalytics: useCallback((msg: string) => trackAnalytics(hook, msg), [hook]),
    setCriticalBanner: useCallback((msg: string) => setCriticalBanner(hook, msg), [hook]),
    clearCriticalBanner: useCallback(() => clearCriticalBanner(hook), [hook]),
    broadcastRecordingState: useCallback((state: AudioState) => broadcastRecordingState(hook, state), [hook]),
    setSubjectId: useCallback((subjectId: string) => setSubjectId(hook, subjectId), [hook]),
  }

  const [state, dispatch] = useSingletonMVUReducer('webRecorder2', {
    initializer: () =>
      initialize({
        canUseWebRecorder,
        subjectId: defaultSubjectId,
        recordingDisclosureAudioPath,
        clientId,
        startRecording,
      }),
    reducer: applyAction,
    sideEffectExecutor: makeSideEffectExecutor(callbacks),
  })

  // For non-React elements that depend on the recording state, we need to observe when
  // recording stops and starts so we can disable or enable the buttons/links.
  const isRecording = state?.mode === 'recording'

  useEffect(() => {
    document.querySelectorAll('[data-disable-when-recording]').forEach((el) => {
      if (isRecording) {
        el.setAttribute('disabled', 'true')
      } else {
        el.removeAttribute('disabled')
      }
    })
  }, [isRecording])

  const currentSubjectId = subjectId(state)

  useWebRecorderPubSubSubscribe<WebRecorder2Action & { subjectId?: string }>(
    'apply-action',
    (action: WebRecorder2Action & { subjectId?: string }) => {
      const appliesToCurrentSubject =
        !('subjectId' in action) || action.action === 'set-subject-id' || action.subjectId === currentSubjectId

      if (appliesToCurrentSubject) {
        dispatch?.(action)
      }
    },
    [currentSubjectId]
  )

  // This will never be true since we're the singleton owner
  if (!state) return <Loading size="sm" />
  return <div></div>
}

// If you're on a /meetings/mtg_* or /prepare/int_ref_* page, we'll default to the subject ID from the URL.
// Otherwise, we should see if you were recently recording another meeting, and if so, continue that.
function defaultSubject(): { subjectId?: string; startRecording: StartRecordingPolicy } {
  const subjectIdFromUrl = window.location.pathname.match(/^\/(meetings|prepare)\/([^/?]+)/)?.at(2)

  if (subjectIdFromUrl && extractSubjectId(subjectIdFromUrl) && meetingIsRecordable()) {
    return {
      subjectId: subjectIdFromUrl,
      startRecording: window.location.search.includes('capture_now=true') ? 'ifNoPreviousRecordingExists' : 'no',
    }
  } else {
    const last24HoursInMs = 24 * 60 * 60 * 1_000
    const recordingToContinue = mostRecentUnsubmittedRecording({ withinLastMs: last24HoursInMs })

    const subjectId = recordingToContinue
      ? 'meetingId' in recordingToContinue
        ? recordingToContinue.meetingId
        : recordingToContinue.intRefId
      : undefined

    return { subjectId, startRecording: recordingToContinue?.recordingInProgress ? 'immediately' : 'no' }
  }
}

function meetingIsRecordable() {
  return document.getElementById('react-meeting-state')?.hasAttribute('data-is-recordable')
}
